import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Mail, CheckCircle } from 'lucide-react';

interface LoadingOverlayProps {
  isOpen: boolean;
  submissionStage: 'city' | 'area' | 'street' | 'sending' | 'complete';
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ isOpen, submissionStage }) => {
  const getStageText = () => {
    switch (submissionStage) {
      case 'city':
        return 'Capturando mapa da cidade...';
      case 'area':
        return 'Capturando mapa do bairro...';
      case 'street':
        return 'Capturando mapa da rua...';
      case 'sending':
        return 'Enviando email...';
      case 'complete':
        return 'Submissão completa!';
      default:
        return 'Processando...';
    }
  };

  const getProgressValue = () => {
    switch (submissionStage) {
      case 'city':
        return 25;
      case 'area':
        return 50;
      case 'street':
        return 75;
      case 'sending':
        return 90;
      case 'complete':
        return 100;
      default:
        return 0;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl p-4 sm:p-8 max-w-md w-full text-center"
          >
            <div className="flex flex-col items-center gap-4 sm:gap-6">
              {submissionStage === 'complete' ? (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1, rotate: [0, 10, 0] }}
                  transition={{ type: 'spring', damping: 10, stiffness: 100 }}
                  className="bg-green-100 rounded-full p-3 sm:p-4"
                >
                  <CheckCircle size={32} className="text-green-500 sm:w-12 sm:h-12" />
                </motion.div>
              ) : (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  className="bg-blue-100 rounded-full p-3 sm:p-4"
                >
                  {submissionStage === 'sending' ? (
                    <Mail size={32} className="text-blue-500 sm:w-12 sm:h-12" />
                  ) : (
                    <Loader2 size={32} className="text-blue-500 sm:w-12 sm:h-12" />
                  )}
                </motion.div>
              )}
              
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                {getStageText()}
              </h2>
              
              {submissionStage !== 'complete' && (
                <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
                  <motion.div
                    initial={{ width: '0%' }}
                    animate={{ width: `${getProgressValue()}%` }}
                    transition={{ duration: 0.5 }}
                    className="h-2 sm:h-3 rounded-full bg-blue-500"
                  />
                </div>
              )}
              
              <p className="text-sm sm:text-base text-gray-600">
                {submissionStage === 'complete' 
                  ? 'Obrigado pelo seu contributo! Redirecionando...'
                  : 'Por favor, aguarde enquanto processamos a sua submissão...'}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingOverlay; 